import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Search, X } from "lucide-react";

export interface MultiSelectDropdownProps {
  label?: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
  searchable?: boolean;
  placeholder?: string;
  className?: string;
  showSelectedTags?: boolean;
}

export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  searchable,
  placeholder,
  className,
  showSelectedTags = true,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered =
    searchable && search
      ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
      : options;

  const toggle = (val: string) => {
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  };

  return (
    <div className={`relative ${className ?? ""}`} ref={containerRef}>
      {label && (
        <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex justify-between items-center ${
          selected.length > 0
            ? "border-orange-300 dark:border-orange-500/40 bg-orange-50 dark:bg-orange-500/10 text-muted-foreground"
            : "border-border bg-card text-muted-foreground"
        }`}
      >
        <span className="truncate">
          {selected.length > 0 ? `${selected.length} selected` : placeholder || "Select..."}
        </span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-xl shadow-xl max-h-60 overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#d7785b]/30"
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto max-h-48">
            {filtered.map((o) => (
              <div
                key={o.value}
                onClick={() => toggle(o.value)}
                className={`flex items-center px-3 py-2 hover:bg-accent cursor-pointer text-sm ${
                  selected.includes(o.value) ? "bg-orange-50 dark:bg-orange-500/10" : ""
                }`}
              >
                <div
                  className={`w-4 h-4 mr-2 flex items-center justify-center border rounded ${
                    selected.includes(o.value) ? "border-[#d7785b] bg-[#d7785b]" : "border-border"
                  }`}
                >
                  {selected.includes(o.value) && <Check size={10} className="text-white" />}
                </div>
                <span className="text-muted-foreground">{o.label}</span>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">No results</div>
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-border">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {showSelectedTags && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map((v) => {
            const opt = options.find((o) => o.value === v);
            return (
              <span
                key={v}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-orange-50 dark:bg-orange-500/10 text-[#d7785b] border border-orange-200 dark:border-orange-500/30 rounded-md"
              >
                {opt?.label || v}
                <button type="button" onClick={() => toggle(v)} className="hover:text-red-600 dark:hover:text-red-400">
                  <X size={10} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
